import { useState } from 'react'
import {
  Button, CloseButton, Dialog, Portal, Stack, Switch, Text,
} from '@chakra-ui/react'
import { useTranslation } from '../i18n'
import { toaster } from './ui/toaster'
import { VIDEO_AI_FEATURES, estimateCostUsd } from '../constants/videoAI'
import { runVideoAIBackfill } from '../services/videoAI'

const formatDuration = (seconds) => {
  const total = Math.round(Number(seconds) || 0)
  const mins = Math.floor(total / 60)
  return mins > 0 ? `${mins}m ${total % 60}s` : `${total}s`
}

/**
 * What a plan costs: each video owes only the detections it is missing, so the
 * server reports seconds per feature and each is priced on its own.
 */
const planCostUsd = (secondsByFeature) => Object.entries(secondsByFeature || {})
  .reduce((sum, [key, seconds]) => sum + estimateCostUsd([key], seconds), 0)

/**
 * Sysadmin-only backfill: brings every video of the event up to the saved
 * settings, running only the detections each one is missing. The click that
 * opens this dialog spends nothing: it runs a dry run first and shows what a
 * real run would cost, because the button inside spends money.
 */
export function VideoAIBackfillDialog({ eventId, disabled = false }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [counting, setCounting] = useState(false)
  const [plan, setPlan] = useState(null)
  const [applyModeration, setApplyModeration] = useState(false)
  const [error, setError] = useState(null)

  const count = async () => {
    setCounting(true)
    setError(null)
    try {
      setPlan(await runVideoAIBackfill({ eventId, dryRun: true }))
    } catch (err) {
      setError(err.message)
    } finally {
      setCounting(false)
    }
  }

  const handleOpenChange = (details) => {
    if (details.open && disabled) return
    setOpen(details.open)
    if (details.open) {
      setError(null)
      setApplyModeration(false)
      count()
    }
  }

  // The dialog exists to take the decision, not to watch the work. The job
  // runs on the server for as long as it takes; its progress is the card
  // under this button and the strip on the Videos page, both live.
  const [starting, setStarting] = useState(false)
  const run = async () => {
    setStarting(true)
    try {
      const out = await runVideoAIBackfill({ eventId, applyModeration })
      setOpen(false)
      toaster.create({
        title: out.started
          ? t('videoAI.backfillStarted', { count: out.total })
          : t('videoAI.backfillNothing'),
        type: 'info',
      })
    } catch (err) {
      toaster.create({
        title: t('videoAI.backfillFailed'), description: err.message,
        type: 'error', duration: 10000,
      })
    } finally {
      setStarting(false)
    }
  }

  const pending = plan ? plan.pendingCount : 0
  const adds = VIDEO_AI_FEATURES
    .filter((f) => (plan?.features || []).includes(f.key))
    .map((f) => t(f.labelKey))
    .join(', ')

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <Button size="sm" variant="outline" disabled={disabled}>{t('videoAI.backfill')}</Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{t('videoAI.backfillTitle')}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap={3}>
                {counting && <Text>{t('videoAI.backfillCounting')}</Text>}
                {error && <Text color="red.400">{t('videoAI.backfillFailed')}: {error}</Text>}
                {!counting && plan && pending === 0 && (
                  <Text>{t('videoAI.backfillNothing')}</Text>
                )}
                {!counting && plan && pending > 0 && (
                  <>
                    <Text>
                      {t('videoAI.backfillSummary', {
                        count: pending,
                        duration: formatDuration(plan.pendingDurationSec),
                      })}
                    </Text>
                    <Text>{t('videoAI.backfillAdds', { features: adds })}</Text>
                    <Text>
                      {t('videoAI.backfillCost', {
                        cost: `$${planCostUsd(plan.pendingSecondsByFeature).toFixed(2)}`,
                      })}
                    </Text>
                    <Text fontSize="sm" color="fg.muted">{t('videoAI.backfillBackground')}</Text>
                    <Switch.Root
                      checked={applyModeration}
                      onCheckedChange={(d) => setApplyModeration(d.checked)}
                      colorPalette="red"
                    >
                      <Switch.HiddenInput /><Switch.Control />
                      <Switch.Label>{t('videoAI.backfillModeration')}</Switch.Label>
                    </Switch.Root>
                    <Text fontSize="sm" color="fg.muted">
                      {t('videoAI.backfillModerationHint')}
                    </Text>
                  </>
                )}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline" size="sm">{t('common.cancel')}</Button>
              </Dialog.ActionTrigger>
              {pending > 0 && (
                <Button size="sm" colorPalette="brand" loading={starting} onClick={run}>
                  {t('videoAI.backfillRun', { count: pending })}
                </Button>
              )}
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
